import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button, Table, Form } from 'react-bootstrap';
import CustomPagination from 'components/CustomPagination';
import { CustomLoading } from 'components/CustomLoading';
import { AddDistroAccountModal, DistroAccountDetailsModal } from './DistroAccountModals';
import FirmUserAccessPage from './FirmUserAccess/FirmUserAccessPage';
import { Download, Search, ChevronUp, ChevronDown, Plus, Users, Eye } from 'lucide-react';
import { generateDistroAdminFeeReport, generateDistroAdminFeeCSV }
  from './Reports/DistroAdminFeeReport';

const filterDistroAccounts = (list, query) => {
  const q = (query ?? '').toLowerCase();
  if (!q) return list;
  return list.filter((item) =>
    item.healthClaimAccountId?.toString().includes(q) ||
    item.healthClaimDistroId?.toString().includes(q) ||
    item.name?.toLowerCase().includes(q) ||
    item.contactEmail?.toLowerCase().includes(q) ||
    item.user_Id?.toString().includes(q) ||
    item.bankAccountNo?.includes(q)
  );
};

const DistroAccountsReport = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('healthClaimAccountId');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showAddDistroModal, setShowAddDistroModal] = useState(false);
  const [userAccessDistro, setUserAccessDistro] = useState(null);
  const [userList, setUserList] = useState([]);
  const [directorRoles, setDirectorRoles] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get('/api/Advisors/distroaccounts');
      setData(response.data.distroAccounts);
      setFilteredData(filterDistroAccounts(response.data.distroAccounts, searchQuery));
      setUserList(response.data.users);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }

    try {
      const directorsResponse = await axios.get('/api/JgDirectors');
      const roles = {};
      const addRole = (id, role, provinceName) => {
        if (!id) return;
        const key = String(id).toLowerCase();
        if (!roles[key]) roles[key] = { rmd: [], bdd: [] };
        roles[key][role].push(provinceName);
      };

      (directorsResponse.data.provinces ?? []).forEach((row) => {
        addRole(row.rmd, 'rmd', row.provinceName);
        addRole(row.bdd1, 'bdd', row.provinceName);
        addRole(row.bdd2, 'bdd', row.provinceName);
      });
      setDirectorRoles(roles);
    } catch (error) {
      setDirectorRoles(null);
      console.error(error);
    }
  };

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    setFilteredData(filterDistroAccounts(data, query));
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    const direction = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
    const sorted = [...filteredData].sort((a, b) => {
      if (a[column] < b[column]) return direction === 'asc' ? -1 : 1;
      if (a[column] > b[column]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setSortColumn(column);
    setSortDirection(direction);
    setFilteredData(sorted);
  };

  const handleShowDetails = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    fetchData();
  };

  const handleCloseAddDistroModal = () => {
    setShowAddDistroModal(false);
    fetchData();
  }

  const handleShowUserAccessModal = (item) => setUserAccessDistro(item);

  const downloadReport = async (distroId) => {
    try {
      const response = await axios.get('/api/Advisors/getdistroadminfeereport', {
        params: {
          distroId: distroId
        }
      });

      generateDistroAdminFeeReport(response.data);
    } catch (error) {
      console.error(error);
    }
  }

  const downloadCSVReport = async (distroId) => {
    try {
      const response = await axios.get('/api/Advisors/getdistroadminfeereport', {
        params: { distroId }
      });

      generateDistroAdminFeeCSV(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const currentRecords = useMemo(() => {
    const indexOfLastRecord = currentPage * itemsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - itemsPerPage;
    return filteredData.slice(indexOfFirstRecord, indexOfLastRecord);
  }, [currentPage, itemsPerPage, filteredData]);

  const nPages = useMemo(() => {
    return Math.ceil(filteredData.length / itemsPerPage);
  }, [filteredData.length, itemsPerPage]);

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  if (userAccessDistro) {
    return (
      <FirmUserAccessPage
        distro={userAccessDistro}
        userList={userList}
        onBack={() => { setUserAccessDistro(null); fetchData(); }}
      />
    );
  }

  return (
    <>
      {loading ? (
        <CustomLoading />
      ) : (
        <>
          <DistroAccountDetailsModal
            showModal={showModal}
            onHide={handleCloseModal}
            selectedItem={selectedItem}
            directorRoles={directorRoles}
          />
          <AddDistroAccountModal
            showModal={showAddDistroModal}
            onHide={handleCloseAddDistroModal}
          />
          <div className="advisor-search-wrapper">
            <Search size={16} />
            <Form.Control
              type="text"
              placeholder="Search by name, ID, email, or bank account..."
              value={searchQuery}
              onChange={handleSearch}
              className="advisor-search-input"
            />
          </div>
          <Button variant="primary" size="sm" className="search-action-btn mb-3" onClick={() => setShowAddDistroModal(true)}>
            <Plus size={14} /> Add Firm Account
          </Button>
          <div className="dashboard-table-container">
            <Table hover className="table table-hover search-results-table">
              <thead>
                <tr>
                  <th className="table-text-content" onClick={() => handleSort('healthClaimAccountId')}>
                    Health Claim Account ID {renderSortIcon('healthClaimAccountId')}
                  </th>
                  <th className="table-text-content" onClick={() => handleSort('healthClaimDistroId')}>
                    Health Claim Firm ID {renderSortIcon('healthClaimDistroId')}
                  </th>
                  <th className="table-text-content" onClick={() => handleSort('name')}>
                    Company Name {renderSortIcon('name')}
                  </th>
                  <th className="table-text-content" onClick={() => handleSort('isActive')}>
                    Is Active {renderSortIcon('isActive')}
                  </th>
                  <th className="table-text-content" onClick={() => handleSort('email')}>
                    Email {renderSortIcon('email')}
                  </th>
                  <th className="table-text-content">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentRecords.map((item, index) => (
                  <tr
                    key={index}
                    className={`table-body-item${!item.isActive ? ' advisor-inactive-row' : ''}`}
                  >
                    <td className="table-text-content">{item.healthClaimAccountId}</td>
                    <td className="table-text-content">{item.healthClaimDistroId}</td>
                    <td className="table-text-content">
                      {item.name}
                    </td>
                    <td className="table-text-content">{item.isActive ? 'Yes' : 'No'}</td>
                    <td className="table-text-content">{item.contactEmail ?? 'N/A'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div className="d-flex align-items-center gap-1 flex-nowrap">
                        <Button onClick={() => handleShowUserAccessModal(item)} variant="outline-primary" size="sm" className="search-action-btn">
                          <Users size={14} /> User Access
                        </Button>
                        <Button onClick={() => handleShowDetails(item)} variant="primary" size="sm" className="search-action-btn">
                          <Eye size={14} /> Details
                        </Button>
                        <Button onClick={() => downloadReport(item.id)} variant="outline-primary" size="sm" className="search-action-btn">
                          <Download size={14} /> PDF
                        </Button>
                        <Button onClick={() => downloadCSVReport(item.id)} variant="outline-primary" size="sm" className="search-action-btn">
                          <Download size={14} /> CSV
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="search-pagination">
            <div className="pagination-info">
              <span>Showing {currentPage * itemsPerPage - itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredData.length)} entries out of {filteredData.length} entries</span>
            </div>
            <CustomPagination
              nPages={nPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </>
      )}
    </>
  );
};

export default DistroAccountsReport;
